// handles confirmation and export downloads
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { updateData } from './firestoreService';
import { documentExport } from './documentExport';
import { serverTimestamp } from 'firebase/firestore';
import { logAuditEventAsync, AuditActions } from './auditLogger';
import { copyToClipboardUtility } from './documentUtils';
import { downloadPdf, downloadDocx, sanitizeFilename } from './exportDownload';
import { Check, Copy, FileDown, FileText } from 'lucide-react';
import SafeHtml from '../components/common/SafeHtml';


const ConfirmDocument = ({ documentData, documentType, patientId, dataHistory, docId, documentComplete, personName, riskAssessmentEnabled = true, companyName = '' }) => {
    const confirmationSection = documentType.sections.find(section => section.type === 'Confirmation');
    const [selectedRiskLevel, setSelectedRiskLevel] = useState(null);
    const [isTextAreaRequired, setIsTextAreaRequired] = useState(false);
    const [textAreaValue, setTextAreaValue] = useState('');
    const [exportResults, setExportResults] = useState(null);
    const [isCopied, setIsCopied] = useState(false);
    const [selectedExportType, setSelectedExportType] = useState(documentType.exports[0]);
    const skipConfirmation = !riskAssessmentEnabled || !confirmationSection;
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const savingRef = useRef(false);
    const directExportStarted = useRef(false);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [isGeneratingDocx, setIsGeneratingDocx] = useState(false);
    const exportTextRef = useRef(null);
    // Preserve the clinician's calendar date, without a UTC conversion.
    const encounterDate = typeof documentData?.documentDate === 'string' ? documentData.documentDate.trim() : '';

    // Direct export when the company or document type requires no confirmation.
    const handleDirectExport = useCallback(async () => {
        if (savingRef.current) return;
        savingRef.current = true;
        setIsSaving(true);
        setSaveError('');
        try {
            const exportData = await documentExport(documentData, documentType, dataHistory);
            await updateData(`patients/${patientId}/documents`, docId, {
                status: 'exported',
                exportedAt: serverTimestamp()
            });

            setExportResults(exportData);
            
            // Audit log: document export
            logAuditEventAsync(AuditActions.DOCUMENT_EXPORT, {
                resourceType: 'document',
                resourceId: docId,
                patientId: patientId,
                metadata: {
                    documentType: documentType?.id || documentType?.name,
                    exportFormat: 'clipboard',
                    riskAssessmentSkipped: true,
                },
            });
        } catch (error) {
            console.error('Error during direct export:', error);
            setSaveError("Couldn't prepare export. Try again.");
        } finally {
            savingRef.current = false;
            setIsSaving(false);
        }
    }, [patientId, docId, documentData, documentType, dataHistory]);

    // Handle direct export when no risk assessment confirmation is required.
    // Retry only on an explicit click after failure, including under StrictMode
    // and parent rerenders while a Firestore write is pending.
    useEffect(() => {
        if (skipConfirmation && !exportResults && !directExportStarted.current) {
            directExportStarted.current = true;
            handleDirectExport();
        }
    }, [skipConfirmation, exportResults, handleDirectExport]);

    const highlightRiskLevel = (details, riskLevel) => {
        const regex = new RegExp(`(${riskLevel})`, 'gi');
        const parts = details.split(regex);
        return parts.map((part, index) =>
            regex.test(part) ? <b key={index}><u>{part}</u></b> : part
        );
    };

    const handleConfirmation = async () => {
        if (savingRef.current || !selectedRiskLevel || (isTextAreaRequired && !textAreaValue.trim())) return;
        savingRef.current = true;
        setIsSaving(true);
        setSaveError('');
    
        const content = {
            riskLevel: selectedRiskLevel,
            details: textAreaValue,
            exportMessage: '<b>Risk:</b> ' + confirmationSection.fields
                .filter(field => field.riskLevel === selectedRiskLevel)
                .map(field => field.details)
                .join('') + '<br><b>Details:</b> ' + textAreaValue,
        };
    
        const dataField = confirmationSection.dataField;
    
        try {
            const updatedDocumentData = {
                ...documentData,
                [dataField]: content,
            };
            const exportData = await documentExport(updatedDocumentData, documentType, dataHistory);
            await updateData(`patients/${patientId}/documents`, docId, {
                [dataField]: content,
                status: 'exported',
                exportedAt: serverTimestamp()
            });

            setExportResults(exportData);
            
            // Audit log: document export
            logAuditEventAsync(AuditActions.DOCUMENT_EXPORT, {
                resourceType: 'document',
                resourceId: docId,
                patientId: patientId,
                metadata: {
                    documentType: documentType?.id || documentType?.name,
                    exportFormat: 'clipboard',
                },
            });
        } catch (error) {
            console.error('Error saving data:', error);
            setSaveError("Couldn't save export details. Try again.");
        } finally {
            savingRef.current = false;
            setIsSaving(false);
        }
    };

    const handleRadioChange = (event) => {
        const selectedField = confirmationSection.fields.find(field => field.riskLevel === event.target.value);
        setSelectedRiskLevel(event.target.value);
        setIsTextAreaRequired(selectedField.requireTextArea);
    };

    const handleTextAreaChange = (event) => {
        setTextAreaValue(event.target.value);
    };

    const handleCopyToClipboard = () => {
        copyToClipboardUtility(buildExportHtml(), setIsCopied, true);
    };

    // All formats use the visible, React-escaped header and the selected sections.
    const buildExportHtml = () => exportTextRef.current?.innerHTML || '';

    const handleDownloadPdf = async () => {
        setIsGeneratingPdf(true);
        try {
            const html = buildExportHtml();
            const filename = sanitizeFilename(documentData?.title || 'document');
            await downloadPdf(html, filename);
            logAuditEventAsync(AuditActions.DOCUMENT_EXPORT, {
                resourceType: 'document',
                resourceId: docId,
                patientId: patientId,
                metadata: {
                    documentType: documentType?.id || documentType?.name,
                    exportFormat: 'pdf',
                },
            });
        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const handleDownloadDocx = async () => {
        setIsGeneratingDocx(true);
        try {
            const html = buildExportHtml();
            const filename = sanitizeFilename(documentData?.title || 'document');
            await downloadDocx(html, filename);
            logAuditEventAsync(AuditActions.DOCUMENT_EXPORT, {
                resourceType: 'document',
                resourceId: docId,
                patientId: patientId,
                metadata: {
                    documentType: documentType?.id || documentType?.name,
                    exportFormat: 'docx',
                },
            });
        } catch (error) {
            console.error('Error generating Word document:', error);
        } finally {
            setIsGeneratingDocx(false);
        }
    };

    return (
        <div>
            {exportResults && documentData ? (
                <div className="standard-container">
                    <div className="modal-header">
                        <h2>Note Export</h2>
                        <div className="export-action-buttons">
                            <button className="export-action-btn" onClick={handleCopyToClipboard}><Copy size={15} /> {isCopied ? 'Copied!' : 'Copy'}</button>
                            <button className="export-action-btn" onClick={handleDownloadPdf} disabled={isGeneratingPdf}><FileDown size={15} /> {isGeneratingPdf ? 'Generating...' : 'PDF'}</button>
                            <button className="export-action-btn" onClick={handleDownloadDocx} disabled={isGeneratingDocx}><FileText size={15} /> {isGeneratingDocx ? 'Generating...' : 'Word'}</button>
                            <button className="export-action-btn export-action-primary" onClick={documentComplete}><Check size={15} /> Done</button>
                        </div>
                    </div>
                    <div className="export-container">
                        <div className="export-tabs">
                            {documentType.exports.map((exportType, index) => (
                                <button
                                    key={index}
                                    className={`export-tab ${selectedExportType === exportType ? 'active' : ''}`}
                                    onClick={() => setSelectedExportType(exportType)}
                                >
                                    {exportType}
                                </button>
                            ))}
                        </div>
                        <div className="export-text" ref={exportTextRef}>
                            <div className="export-header" style={{ marginBottom: 16 }}>
                                <h2>{documentData.title || 'Document'}</h2>
                                <p><strong>Patient:</strong> {personName || ''}</p>
                                {encounterDate && <p><strong>Date:</strong> {encounterDate}</p>}
                                {companyName && <p><strong>Organization:</strong> {companyName}</p>}
                                <hr />
                            </div>
                            {Object.entries(exportResults[selectedExportType]).map(([key, section], index) => (
                                <div key={index} className='export-section'>
                                    {section.title && <h3>{section.title}</h3>}
                                    {section.data && <SafeHtml html={section.data} />}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="export-action-buttons">
                        <button className="export-action-btn" onClick={handleCopyToClipboard}><Copy size={15} /> {isCopied ? 'Copied!' : 'Copy'}</button>
                        <button className="export-action-btn" onClick={handleDownloadPdf} disabled={isGeneratingPdf}><FileDown size={15} /> {isGeneratingPdf ? 'Generating...' : 'PDF'}</button>
                        <button className="export-action-btn" onClick={handleDownloadDocx} disabled={isGeneratingDocx}><FileText size={15} /> {isGeneratingDocx ? 'Generating...' : 'Word'}</button>
                        <button className="export-action-btn export-action-primary" onClick={documentComplete}><Check size={15} /> Done</button>
                    </div>
                </div>
            ) : skipConfirmation ? (
                <div className="standard-container" aria-busy={isSaving}>
                    <h2>Export</h2>
                    {saveError ? <>
                        <p className="modal-error-message" role="alert">{saveError}</p>
                        <button className="button-confirm" onClick={handleDirectExport} disabled={isSaving}>Retry</button>
                    </> : <p role="status">Preparing export…</p>}
                </div>
            ) : (
                confirmationSection && (
                    <div className="standard-container" aria-busy={isSaving}>
                        <div className="risk-assessment-header" style={{ marginBottom: "12px" }}>
                            <span style={{ fontSize: "var(--text-xl)" }}>
                                <strong>{confirmationSection.title}</strong>
                            </span>
                        </div>
                        {confirmationSection.details && (
                            <h4>{confirmationSection.details}</h4>
                        )}
                        {confirmationSection.fields.map((field, index) => (
                            <div key={index} className="standard-radio">
                                <input
                                    type="radio"
                                    name="riskLevel"
                                    value={field.riskLevel}
                                    aria-label={field.riskLevel}
                                    checked={selectedRiskLevel === field.riskLevel}
                                    disabled={isSaving}
                                    onChange={handleRadioChange}
                                />
                                <span className="standard-radio-text">{highlightRiskLevel(field.details, field.riskLevel)}</span>
                            </div>
                        ))}
                        <div className="modal-textarea">
                            <textarea
                                id="risk-assessment-details"
                                name="riskAssessmentDetails"
                                aria-label="Risk assessment details"
                                className="standard-textarea"
                                placeholder={isTextAreaRequired ? "Required details..." : "Optional details..."}
                                required={isTextAreaRequired}
                                value={textAreaValue}
                                readOnly={isSaving}
                                onChange={handleTextAreaChange}
                            ></textarea>
                        </div>
                        {saveError && <p className="modal-error-message" role="alert">{saveError}</p>}
                        <div className="modal-button-container">
                            <button
                                className="button-confirm"
                                onClick={handleConfirmation}
                                disabled={isSaving || !selectedRiskLevel || (isTextAreaRequired && !textAreaValue.trim())}
                            >
                                {isSaving ? 'Saving…' : saveError ? 'Retry' : 'Export...'}
                            </button>
                        </div>
                    </div>
                )
            )}
        </div>
    );
};

export default ConfirmDocument;
